import React, { useCallback, useEffect, useMemo, useRef, useState, useTransition } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { graphql, useLazyLoadQuery, usePaginationFragment } from 'react-relay';

import { type AnalyticsEventPayload } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import Lozenge from '@atlaskit/lozenge';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Flex, Text } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import type { Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { type AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';

import {
	type BrowseCategory,
	type CustomAgentCardMenuOption,
	type AgentRendererProps,
	type UIContext,
} from '../../../common/types.ts';
import { AgentCard } from '../../../common/ui/agent-card/index.tsx';
import {
	getPaginationQueryInput,
	mapAgentGqlToAgent,
} from '../../../common/utils/agent-query-helper/index.ts';
import { useAgentUrlActions } from '../../../controllers/agent-url-actions/index.tsx';
import { useAgentVerification } from '../../../controllers/agent-verification/index.tsx';
import { AGENTS_CONTAINER_ID } from '../../../controllers/agents/constants.ts';
import { useBrowseAgentsUIStore } from '../../../controllers/browse-agents-ui/index.tsx';
import { isAutodevTemplateAgent as isAutodevTemplateAgentUtil } from '../../../controllers/browse-agents-ui/utils.ts';
import { useInfiniteScroll } from '../../../controllers/infinite-scroll/index.tsx';

import type { browseAgentsContent_AtlassianConversationAssistantAgent$key } from './__generated__/browseAgentsContent_AtlassianConversationAssistantAgent.graphql.ts';
import type { browseAgentsContentPaginationQuery } from './__generated__/browseAgentsContentPaginationQuery.graphql.ts';
import type { browseAgentsContentQuery } from './__generated__/browseAgentsContentQuery.graphql.ts';
import { AgentGroupedList } from './agent-grouped-list/index.tsx';
import { NoCreatedAgentsEmptyState } from './empty-states/no-created.tsx';
import { NoStarredAgentsEmptyState } from './empty-states/no-starred.tsx';
import { NoVerifiedAgentsEmptyState } from './empty-states/no-verified.tsx';
import messages from './messages.ts';
import { BrowseAgentsContentNoResults } from './no-results/index.tsx';

/**
 * We want to ensure the payload is consistent between the old ux and new
 */
export const getSearchAnalyticsPayload = ({
	searchValue,
	viewContext,
	category,
}: {
	searchValue: string;
	viewContext: UIContext;
	category: string;
}): AnalyticsEventPayload => {
	return {
		action: 'searched',
		actionSubject: 'input',
		actionSubjectId: 'browseAgentsSearchInput',
		attributes: {
			interactionSource: 'browse-agents-content',
			searchValueLength: searchValue.length,
			viewContext,
			category,
		},
	};
};

const styles = cssMap({
	emptyStateHeight: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		height: '692px',
	},

	emptyStateContainer: {
		width: '100%',
		maxWidth: '600px',
		textAlign: 'center',
	},

	templateTitle: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.100'),
	},
	browseWrapper: {
		alignItems: 'center',
		minHeight: '50vh',
		display: 'flex',
		boxSizing: 'border-box',
		flexDirection: 'column',
		justifyContent: 'start',
		position: 'relative',
		overflow: 'auto',
		maxHeight: '100%',
	},
	browseWrapperRefresh: {
		overflow: 'hidden',
	},
	browseWrapperRefreshFocusOffset: {
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
	},
	browseWrapperModal: {
		paddingBottom: token('space.300'),
	},

	loadMoreSpinnerWrapper: {
		minHeight: '50px',
	},
	loadMoreSpinner: {
		paddingBottom: token('space.300'),
	},
});

type BrowseAgentsContentProps = {
	onDeleteAgent: (agent: Agent, source: string) => void;
	onClickChat: (agent: Agent) => void;
	onClickAgent?: (agent: Agent) => void;
	singleColumn?: boolean;
	agentCardRole?: 'link' | 'button';
	getAgentCardAriaLabel?: (agentName: string, agentJiraContext?: AgentRendererProps) => string;
	triggerAgentModalButton?: React.ReactNode | null;
	agentTabs?: React.ReactNode | null;
	uiContext?: UIContext;
	selectedTab: BrowseCategory;
	onTabChange: (tab: BrowseCategory) => void;
	customAgentDropdownMenuOptions?: CustomAgentCardMenuOption[];
	renderAgentCardFooter?: (props: AgentRendererProps) => React.ReactNode;
	enableJiraAgentFields?: boolean;
} & AssistanceServiceInputParam;

const PAGE_SIZE = 30;

export const BrowseAgentsContent = ({
	assistanceServiceParams,
	onClickChat,
	onClickAgent,
	onDeleteAgent,
	singleColumn,
	agentCardRole,
	getAgentCardAriaLabel,
	searchValue,
	uiContext,
	selectedTab,
	onTabChange,
	customAgentDropdownMenuOptions,
	renderAgentCardFooter,
	enableJiraAgentFields,
}: BrowseAgentsContentProps & {
	searchValue: string;
}): React.JSX.Element | null => {
	di();

	const [_, { setSearchValue, setSelectedAgent }] = useBrowseAgentsUIStore();
	const headers = new Headers(assistanceServiceParams.headers);
	const maybeWorkspaceId = headers.get('x-workspace-id');

	// Transitioning the search value to show filtered results while query is pending
	const [isSearchPending, startSearchTransition] = useTransition();
	const [transitionedSearchValue, setTransitionedSearchValue] = useState(searchValue);

	useEffect(() => {
		startSearchTransition(() => {
			setTransitionedSearchValue(searchValue);
		});
	}, [searchValue]);

	const preloadedRef = useLazyLoadQuery<browseAgentsContentQuery>(
		graphql`
			query browseAgentsContentQuery(
				$cloudIdString: String!
				$cloudId: ID!
				$input: AgentStudioAgentQueryInput
				$product: String
				$workspaceId: String
				$skipUserPermissions: Boolean = true
				$enableJiraAgentFields: Boolean = false
			) {
				...browseAgentsContent_AtlassianConversationAssistantAgent
					@arguments(
						cloudId: $cloudIdString
						input: $input
						product: $product
						workspaceId: $workspaceId
						enableJiraAgentFields: $enableJiraAgentFields
						cloudIdAsId: $cloudId
					)
				atlassianStudio_userSiteContext(cloudId: $cloudId) @skip(if: $skipUserPermissions) {
					... on AtlassianStudioUserSiteContextOutput {
						userPermissions {
							# eslint-disable-next-line @atlassian/relay/must-colocate-fragment-spreads
							...agentVerificationDropdownItem_AtlaskitRovoAgentComponents_userPermissionsRef
						}
					}
				}
			}
		`,
		{
			cloudIdString: assistanceServiceParams.siteId,
			cloudId: assistanceServiceParams.siteId,
			input: getPaginationQueryInput({ tab: selectedTab, searchValue: transitionedSearchValue }),
			product: assistanceServiceParams.product,
			workspaceId: maybeWorkspaceId,
			skipUserPermissions: !fg('rovo_agents_agent_verification'),
			enableJiraAgentFields: Boolean(enableJiraAgentFields),
		},
		{
			// Same @connection key as sidebar favourites; force a fresh query when tab/search change after e.g. Jira custom tab → all-agents.
			fetchKey: `${uiContext ?? 'browse-agents'}-${selectedTab}-${transitionedSearchValue}`,
		},
	);

	// Extract user permissions ref for agent verification
	const userPermissionsRef = preloadedRef?.atlassianStudio_userSiteContext?.userPermissions;

	const { data, hasNext, isLoadingNext, loadNext } = usePaginationFragment<
		browseAgentsContentPaginationQuery,
		browseAgentsContent_AtlassianConversationAssistantAgent$key
	>(
		/* eslint-disable @atlassian/relay/unused-fields */
		/* These fields are used in the mapAgentGqlToAgent function */
		graphql`
			fragment browseAgentsContent_AtlassianConversationAssistantAgent on Query
			@argumentDefinitions(
				first: { type: "Int", defaultValue: 30 }
				after: { type: "String" }
				cloudId: { type: "String!" }
				input: { type: "AgentStudioAgentQueryInput" }
				product: { type: "String" }
				workspaceId: { type: "String" }
				enableJiraAgentFields: { type: "Boolean!" }
				cloudIdAsId: { type: "ID!" }
			)
			@refetchable(queryName: "browseAgentsContentPaginationQuery") {
				agentStudio_getAgents(
					cloudId: $cloudId
					input: $input
					first: $first
					after: $after
					product: $product
					workspaceId: $workspaceId
				)
					@optIn(to: "AgentStudio")
					# This connection key is shared between the panel, fullscreen, modal and favourites in the modal - be careful when changing it
					@connection(key: "paginatedAgentsList_agentStudio_getAgents") {
					edges {
						node {
							id
							name
							deactivated
							description
							isFavourite
							creatorType
							conversationStarters
							identityAccountId
							externalConfigReference
							creator {
								id
							}
							icon
							authoringTeam {
								id
								profileUrl
								displayName
								smallAvatarImageUrl
							}
							forgeCreator
							# eslint-disable-next-line @atlassian/relay/must-colocate-fragment-spreads
							...agentVerificationDropdownItem_AtlaskitRovoAgentComponents_agentRef
							# eslint-disable-next-line @atlassian/relay/must-colocate-fragment-spreads
							...agentVerified_AtlaskitRovoAgentComponents
							# eslint-disable-next-line @atlassian/relay/must-colocate-fragment-spreads
							...agentCard_AtlassianConversationAssistantAgent_agentRef
								@arguments(enableJiraAgentFields: $enableJiraAgentFields, cloudId: $cloudIdAsId)
						}
					}
				}
			}
		`,
		preloadedRef,
	);

	const isMatchingAgent = useCallback((query: string, name?: string | null): boolean => {
		return name?.toLocaleLowerCase().includes(query.toLocaleLowerCase()) ?? false;
	}, []);

	const agentData = useMemo(() => {
		const agents =
			data?.agentStudio_getAgents?.edges
				.map((edge) => edge.node)
				.filter((node): node is NonNullable<typeof node> => node !== null) ?? [];

		if (!isSearchPending) {
			return agents;
		}

		return agents.filter((agent) => isMatchingAgent(searchValue, agent.name));
	}, [data, isSearchPending, isMatchingAgent, searchValue]);

	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();

	const inputRef = useRef<HTMLInputElement>(null);
	const { siteId } = assistanceServiceParams;

	const { onEditAgent, onCopyAgent, onDuplicateAgent } = useAgentUrlActions({
		siteId,
		source: 'agentCard',
		xpcBridge: 'rovoAgents-browseAgentsModal',
	});

	const { updateVerifiedAgentsConnection } = useAgentVerification({
		assistanceServiceParams,
	});

	// Clear search input when selected tab changes
	useEffect(() => {
		if (inputRef.current) {
			inputRef.current.value = '';
		}
	}, [selectedTab]);

	const isTemplateTabActive = selectedTab === 'templates';
	const isFirstLoad = !hasNext && isLoadingNext;

	const { loadingLabel } = useMemo(() => {
		const loadingLabel = isFirstLoad
			? formatMessage(messages.loadingAgents)
			: formatMessage(messages.loadMoreButtonLoading);

		return {
			loadingLabel,
		};
	}, [isFirstLoad, formatMessage]);

	// Due to pagination, category is considered empty if there's no results and you're not actually searching for anything
	// "No results" is treated differently, where no agents are returned but you're searching for something
	// Use the deferred value so these flags stay in sync with the data currently rendered.
	const isContentEmpty =
		!isLoadingNext && !isSearchPending && agentData.length === 0 && searchValue.trim() === '';

	const hasNoResults =
		!isLoadingNext && !isSearchPending && agentData.length === 0 && searchValue.trim() !== '';

	const groupedAgentData = useMemo(() => {
		if (selectedTab === 'all-agents') {
			return [
				{
					agents: agentData,
					contentRenderOverride: hasNoResults ? (
						<BrowseAgentsContentNoResults
							title={formatMessage(messages.noResults, { searchValue })}
						/>
					) : undefined,
				},
			];
		} else {
			return [
				{
					agents: agentData,
					contentRenderOverride: hasNoResults ? (
						<BrowseAgentsContentNoResults
							title={formatMessage(messages.noResults, { searchValue })}
							onTabChange={onTabChange}
						/>
					) : undefined,
				},
			];
		}
	}, [agentData, selectedTab, hasNoResults, searchValue, onTabChange, formatMessage]);

	// Tracks the count of agents before a Load More request, so we can identify
	// and focus the first newly loaded card once the response arrives.
	const [pendingFocusCount, setPendingFocusCount] = useState<number | null>(null);

	// Ref to the first newly loaded card's primary interactive element
	const firstNewCardRef = useRef<HTMLButtonElement>(null);

	const handleLoadMore = useCallback(() => {
		if (isLoadingNext || !hasNext) {
			return;
		}
		if (fg('a11y_33284_inifinite_scrolling_functionality')) {
			setPendingFocusCount(agentData.length);
		}
		loadNext(PAGE_SIZE);
	}, [isLoadingNext, hasNext, loadNext, agentData.length]);

	// Old infinite scroll behavior (behind feature gate)
	const loadMoreRefCallback = useInfiniteScroll({
		onScrollTo: () => {
			handleLoadMore();
		},
	});

	// Clear pending focus when tab or search changes before load completes
	useEffect(() => {
		if (fg('a11y_33284_inifinite_scrolling_functionality')) {
			setPendingFocusCount(null);
		}
	}, [selectedTab, transitionedSearchValue]);

	// Focus the first newly loaded card once new data arrives
	useEffect(() => {
		if (!fg('a11y_33284_inifinite_scrolling_functionality')) {
			return;
		}
		if (pendingFocusCount === null) {
			return;
		}
		if (agentData.length > pendingFocusCount) {
			firstNewCardRef.current?.focus();
			setPendingFocusCount(null);
		} else if (!isLoadingNext) {
			setPendingFocusCount(null);
		}
	}, [agentData.length, isLoadingNext, pendingFocusCount]);

	const isLoadingMoreAgents = useMemo(() => {
		return isLoadingNext || isSearchPending;
	}, [isLoadingNext, isSearchPending]);

	if (isContentEmpty) {
		if (selectedTab === 'starred-agents') {
			return (
				<Box xcss={styles.emptyStateHeight}>
					<NoStarredAgentsEmptyState
						singleColumn={singleColumn}
						onSeeAllAgentsClick={(analyticsEvent) => {
							onTabChange('all-agents');
							setSearchValue('');
							sendAnalyticsEvent(
								analyticsEvent.update({
									actionSubjectId: 'noStarredAgentsButton',
									attributes: {
										browseCategory: 'all-agents',
									},
								}),
							);
						}}
					/>
				</Box>
			);
		} else if (selectedTab === 'my-agents') {
			return (
				<Box xcss={styles.emptyStateHeight}>
					<NoCreatedAgentsEmptyState
						siteId={siteId}
						navigationSource="browse-agents-content-my-agents"
						singleColumn={singleColumn}
					/>
				</Box>
			);
		} else if (selectedTab === 'verified-agents') {
			return (
				<Box xcss={styles.emptyStateHeight}>
					<NoVerifiedAgentsEmptyState
						singleColumn={singleColumn}
						onSeeAllAgentsClick={(analyticsEvent) => {
							onTabChange('all-agents');
							setSearchValue('');
							sendAnalyticsEvent(
								analyticsEvent.update({
									actionSubjectId: 'noVerifiedAgentsButton',
									attributes: {
										browseCategory: 'all-agents',
									},
								}),
							);
						}}
					/>
				</Box>
			);
		} else {
			return null;
		}
	}

	return (
		<Box
			xcss={cx(
				styles.browseWrapper,
				uiContext === 'agents-modal' && styles.browseWrapperModal,
				uiContext === 'agents-sidebar' && styles.browseWrapperRefresh,
				uiContext === 'agents-sidebar' &&
					fg('rovo_agents_browse_sidebar_focus_offset') &&
					styles.browseWrapperRefreshFocusOffset,
			)}
			{...(fg('jfp_a11y_team_browse_agents_tabpanel') && {
				id: AGENTS_CONTAINER_ID,
				role: 'tabpanel',
				'aria-labelledby': `tab-${selectedTab}`,
			})}
		>
			<Box>
				{isTemplateTabActive && (
					<Box paddingBlockStart="space.200" xcss={styles.templateTitle}>
						<Flex alignItems="center" gap="space.050">
							<Heading size="small" as="h2">
								{formatMessage(messages.templatesTitle)}
							</Heading>
							<Lozenge appearance="discovery">{formatMessage(messages.betaLozengeTitle)}</Lozenge>
						</Flex>
						<Text>{formatMessage(messages.templatesDescription)}</Text>
					</Box>
				)}
			</Box>

			<AgentGroupedList
				singleColumn={singleColumn}
				data={groupedAgentData}
				renderAgentCard={(agentNode, index) => {
					// TODO: Remove this mapping once callbacks (onClickChat, onDeleteAgent, etc.) are
					// refactored to accept agent IDs/ARIs instead of the full Agent object.
					// AgentCard already maps internally via useFragment, so this is redundant.
					const agent = mapAgentGqlToAgent(agentNode);
					return (
						<AgentCard
							key={agentNode.id}
							focusRef={
								index === pendingFocusCount && fg('a11y_33284_inifinite_scrolling_functionality')
									? firstNewCardRef
									: undefined
							}
							siteId={siteId}
							assistanceServiceParams={assistanceServiceParams}
							agentCardRole={agentCardRole}
							getAgentCardAriaLabel={getAgentCardAriaLabel}
							onViewAgent={() => {
								setSelectedAgent(agent);
								sendAnalyticsEvent({
									action: 'clicked',
									actionSubject: 'button',
									actionSubjectId: 'viewAgentButton',
									attributes: {
										agentId: agent.id,
										source: 'agentCard',
										category: selectedTab,
									},
								});
							}}
							// TODO https://product-fabric.atlassian.net/browse/RAGE-654 remove this and use util function
							isAutodevTemplate={isAutodevTemplateAgentUtil(agent)}
							onClickChat={() => {
								onClickChat(agent);
								sendAnalyticsEvent({
									action: 'clicked',
									actionSubject: 'button',
									actionSubjectId: 'agentCard',
									attributes: {
										agentId: agent.id,
										interactionSource: 'browse-agents-content',
										category: selectedTab,
									},
								});
							}}
							onClickAgent={
								onClickAgent
									? () => {
											onClickAgent(agent);
											sendAnalyticsEvent({
												action: 'clicked',
												actionSubject: 'button',
												actionSubjectId: 'agentCard',
												attributes: {
													agentId: agent.id,
													interactionSource: 'browse-agents-content',
													category: selectedTab,
												},
											});
										}
									: undefined
							}
							onEditAgent={() => onEditAgent(agent.id)}
							onCopyAgent={() =>
								onCopyAgent({
									agentId: agent.id,
									agentIdentityAccountId: agent.identity_account_id,
								})
							}
							onDuplicateAgent={async () => {
								// TODO RAGE-2852 we can get agentARI guaranteed from useFragment
								await onDuplicateAgent(agent.id, agent.agentAri ?? '');
							}}
							onDeleteAgent={() => onDeleteAgent(agent, 'agentCard')}
							hideDescription={uiContext === 'agents-sidebar'}
							agentRef={agentNode}
							userPermissionsRef={userPermissionsRef ?? null}
							onVerificationSuccess={(verified: boolean) => {
								const agentAri = agent.agentAri;
								if (agentAri) {
									updateVerifiedAgentsConnection(agentAri, verified);
								}
							}}
							customAgentDropdownMenuOptions={customAgentDropdownMenuOptions}
							renderFooter={renderAgentCardFooter}
						/>
					);
				}}
			/>

			{fg('a11y_33284_inifinite_scrolling_functionality') ? (
				(hasNext || isSearchPending) && (
					<Box xcss={styles.loadMoreSpinnerWrapper} testId="load-more-agents-wrapper">
						<Button
							onClick={handleLoadMore}
							isDisabled={isLoadingMoreAgents}
							appearance="primary"
							testId="load-more-agents-button"
							aria-label={formatMessage(messages.loadMoreButtonReady)}
						>
							{isLoadingMoreAgents ? (
								<Spinner
									size="small"
									label={loadingLabel}
									interactionName="loading-paginated-agents"
								/>
							) : (
								formatMessage(messages.loadMoreButtonReady)
							)}
						</Button>
					</Box>
				)
			) : (
				<Box
					ref={loadMoreRefCallback}
					xcss={styles.loadMoreSpinnerWrapper}
					testId="load-more-spinner-wrapper"
				>
					<Box xcss={styles.loadMoreSpinner}>
						{isLoadingMoreAgents && (
							<Spinner
								size="small"
								label={loadingLabel}
								interactionName="loading-paginated-agents"
							/>
						)}
					</Box>
				</Box>
			)}
		</Box>
	);
};
